/**
 * Per-organization theming for the public matchmaking form.
 *
 * An organiser sets `organizations.brand_color` and we tint the form with it.
 * The catch is that a brand colour is chosen to look good in a logo, not to be
 * legible behind white button text — pale yellows and mint greens are common,
 * and both would produce an unreadable "Send my picks" button.
 *
 * So the accent is accepted, but the text placed on it is chosen by measuring
 * contrast rather than assuming white. If even the better of black and white
 * fails WCAG AA against the brand colour, the colour is darkened until it
 * passes. The organiser gets their colour; the attendee gets a readable page.
 */

export interface Rgb {
  r: number
  g: number
  b: number
}

export interface BrandTheme {
  /** The accent actually applied, which may be a darkened brand colour. */
  accent: string
  /** A darker variant for hover and active states. */
  accentStrong: string
  /** Whichever of white/near-black reads on `accent`. */
  accentText: string
  /** A very light tint of the accent, for selected-row backgrounds. */
  accentWash: string
  /** True when the requested colour had to be adjusted to stay legible. */
  adjusted: boolean
}

const HEX_PATTERN = /^#?([0-9a-f]{6})$/i

/** WCAG AA for normal-size text. */
const MIN_CONTRAST = 4.5

/** The attendee ink colour, used as the dark alternative to white text. */
const NEAR_BLACK = '#101a44'
const NEAR_BLACK_RGB: Rgb = { r: 0x10, g: 0x1a, b: 0x44 }
const WHITE = '#ffffff'

export function parseHexColor(value: string | null | undefined): Rgb | null {
  if (!value) return null
  const match = HEX_PATTERN.exec(value.trim())
  if (!match?.[1]) return null

  const int = Number.parseInt(match[1], 16)
  return {
    r: (int >> 16) & 0xff,
    g: (int >> 8) & 0xff,
    b: int & 0xff,
  }
}

export function toHex({ r, g, b }: Rgb): string {
  const channel = (value: number) =>
    Math.max(0, Math.min(255, Math.round(value)))
      .toString(16)
      .padStart(2, '0')
  return `#${channel(r)}${channel(g)}${channel(b)}`
}

/** Relative luminance, per WCAG 2.1. */
export function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (value: number) => {
    const c = value / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/** Contrast ratio between two colours, from 1 (identical) to 21 (black on white). */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const lighter = Math.max(relativeLuminance(a), relativeLuminance(b))
  const darker = Math.min(relativeLuminance(a), relativeLuminance(b))
  return (lighter + 0.05) / (darker + 0.05)
}

function mix(color: Rgb, target: Rgb, amount: number): Rgb {
  return {
    r: color.r + (target.r - color.r) * amount,
    g: color.g + (target.g - color.g) * amount,
    b: color.b + (target.b - color.b) * amount,
  }
}

const BLACK: Rgb = { r: 0, g: 0, b: 0 }
const WHITE_RGB: Rgb = { r: 255, g: 255, b: 255 }

/** The better of white and near-black against a background. */
function bestTextOn(background: Rgb): { color: string; ratio: number } {
  const white = contrastRatio(background, WHITE_RGB)
  const dark = contrastRatio(background, NEAR_BLACK_RGB)
  return white >= dark ? { color: WHITE, ratio: white } : { color: NEAR_BLACK, ratio: dark }
}

/**
 * Builds the theme for a brand colour, falling back to the default royal blue
 * when none is set or the value is unparseable.
 *
 * Returns `null` when there is nothing to override, so the caller can leave the
 * stylesheet defaults alone rather than re-applying identical values.
 */
export function buildBrandTheme(brandColor: string | null | undefined): BrandTheme | null {
  const parsed = parseHexColor(brandColor)
  if (!parsed) return null

  let accent = parsed
  let adjusted = false
  let best = bestTextOn(accent)

  // Darken toward black in small steps until white text is comfortably legible.
  // Ten steps of 8% is enough to rescue any colour short of pure white, and
  // stops well before the accent stops resembling what was chosen.
  for (let step = 0; step < 10 && best.ratio < MIN_CONTRAST; step++) {
    accent = mix(accent, BLACK, 0.08)
    adjusted = true
    best = bestTextOn(accent)
  }

  return {
    accent: toHex(accent),
    accentStrong: toHex(mix(accent, BLACK, 0.18)),
    accentText: best.color,
    accentWash: toHex(mix(accent, WHITE_RGB, 0.92)),
    adjusted,
  }
}

/** CSS custom properties to spread onto the public form's root element. */
export function brandThemeStyle(theme: BrandTheme | null): Record<string, string> {
  if (!theme) return {}
  return {
    '--attendee-accent': theme.accent,
    '--attendee-accent-strong': theme.accentStrong,
    '--attendee-accent-text': theme.accentText,
    '--attendee-accent-wash': theme.accentWash,
  }
}
